"use client";

import {
  isFacebookBrowser,
  isIOSSafari,
  isMobile,
  isRunningOnClient,
  isSamsungBrowser,
} from "@/utils/browser";
import { notifyError } from "@/utils/monitoring";
import { trackLegacyEvent } from "@analytics";
import type React from "react";
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: Array<string>;
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export type CustomPromptModalType =
  | "ios"
  | "samsung"
  | "facebook"
  | "other"
  | null;

export type PromptInstallOutcome =
  | "accepted"
  | "dismissed"
  | "custom_instructions_triggered"
  | "error"
  | "not_applicable";

export type PWAInstallDisplayMode =
  | "browser"
  | "standalone"
  | "minimal-ui"
  | "fullscreen"
  | "window-controls-overlay"
  | "twa"
  | "unknown";

// Covers any changes to the manifest.json file for PWAs.
// Any of the below are valid PWA display modes across mobile and desktop
const PWA_SHELL_MODES: PWAInstallDisplayMode[] = [
  "standalone",
  "minimal-ui",
  "fullscreen",
  "window-controls-overlay",
];

interface PWAInstallContextType {
  installPromptEvent: BeforeInstallPromptEvent | null;
  setInstallPromptEvent: (event: BeforeInstallPromptEvent | null) => void;
  isPWAInstalled: boolean;
  promptInstall: () => Promise<PromptInstallOutcome>;
  isCustomInstructionsModalOpen: boolean;
  customPromptModalType: CustomPromptModalType;
  closeCustomInstructionsModal: () => void;
  pwaDisplayMode: PWAInstallDisplayMode;
}

const PWAInstallContext = createContext<PWAInstallContextType | undefined>(
  undefined,
);

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

// Function to detect PWA display mode using matchMedia
const getPWADisplayMode = (): PWAInstallDisplayMode => {
  if (!isRunningOnClient()) return "unknown";

  if (document.referrer.startsWith("android-app://")) return "twa";

  const matchedMode = PWA_SHELL_MODES.find(
    (mode) => window.matchMedia(`(display-mode: ${mode})`).matches,
  );

  return matchedMode ?? "unknown";
};

export const PWAInstallProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [installPromptEvent, setInstallPromptEvent] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);
  const [isCustomInstructionsModalOpen, setIsCustomInstructionsModalOpen] =
    useState(false);
  const [customPromptModalType, setCustomPromptModalType] =
    useState<CustomPromptModalType>(null);
  const [pwaDisplayMode, setPwaDisplayMode] = useState<PWAInstallDisplayMode>(
    () => getPWADisplayMode(),
  );

  useEffect(() => {
    if (!isRunningOnClient()) return;

    const isFullscreenDesktop = !isMobile() && pwaDisplayMode === "fullscreen";

    const isPWAShell =
      (PWA_SHELL_MODES.includes(pwaDisplayMode) ||
        (window.navigator as NavigatorWithStandalone).standalone === true) &&
      !isFullscreenDesktop;

    if (isPWAShell) {
      // This event tells us if the user opened the PWA directly, rather than from
      // a browser shortcut.
      trackLegacyEvent(
        {
          screen: "PWA",
          userJourney: "",
          action: "Opened",
          extras: {
            pwaDisplayMode,
            userAgent: window.navigator.userAgent,
          },
        },
        "",
      );
    }

    // Check if the PWA is already installed (e.g., for standalone mode)
    if (isPWAShell) {
      setIsPWAInstalled(true);
    }
  }, [pwaDisplayMode]);

  useEffect(() => {
    if (!isRunningOnClient()) return;

    // These event listeners will detect a transfer from one display mode to another.
    // This covers the following cases:
    // 1. First-time install and transfer to PWA
    // 2. Opening the app in a browser, then choosing to open in the PWA
    const mediaQueryLists = PWA_SHELL_MODES.map((mode) =>
      window.matchMedia(`(display-mode: ${mode})`),
    );

    const handleDisplayModeChange = () => {
      setPwaDisplayMode(getPWADisplayMode());
    };

    // Add the event listeners
    for (const mql of mediaQueryLists) {
      mql.addEventListener("change", handleDisplayModeChange);
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPromptEvent(event as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setIsPWAInstalled(true);
      // Optionally clear the prompt event if the app is installed via other means
      setInstallPromptEvent(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      // Clean up
      for (const mql of mediaQueryLists) {
        mql.removeEventListener("change", handleDisplayModeChange);
      }

      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const promptInstall = async (): Promise<PromptInstallOutcome> => {
    if (installPromptEvent) {
      try {
        trackLegacyEvent(
          {
            screen: "PWAInstallPrompt",
            userJourney: "PWAInstallation",
            action: "Opened",
            extras: {
              platform: installPromptEvent.platforms[0],
            },
          },
          "Play",
        );
        installPromptEvent.prompt();
        const { outcome } = await installPromptEvent.userChoice;
        trackLegacyEvent(
          {
            screen: "PWAInstallPrompt",
            userJourney: "PWAInstallation",
            action: "Pressed",
            extras: {
              platform: installPromptEvent.platforms[0],
              outcome,
            },
          },
          "Play",
        );
        if (outcome === "accepted") {
          setInstallPromptEvent(null); // Clear event after successful install
          setIsPWAInstalled(true);
          return "accepted";
        }
        // Don't clear the event if dismissed, allow retry
        trackLegacyEvent(
          {
            screen: "PWAInstallPrompt",
            userJourney: "PWAInstallation",
            action: "Dismissed",
            extras: {
              platform: installPromptEvent.platforms[0],
              outcome,
            },
          },
          "Play",
        );
        return "dismissed";
      } catch (error) {
        notifyError(error, "discoverFeedError");
        setInstallPromptEvent(null); // Clear on error to prevent repeated failures with the same event
        setIsCustomInstructionsModalOpen(true);
        return "error";
      }
    } else if (isIOSSafari()) {
      setCustomPromptModalType("ios");
      setIsCustomInstructionsModalOpen(true);
      return "custom_instructions_triggered";
    } else if (isSamsungBrowser()) {
      setCustomPromptModalType("samsung");
      setIsCustomInstructionsModalOpen(true);
      return "custom_instructions_triggered";
    } else if (isFacebookBrowser()) {
      setCustomPromptModalType("facebook");
      setIsCustomInstructionsModalOpen(true);
      return "custom_instructions_triggered";
    } else if (isRunningOnClient()) {
      setCustomPromptModalType("other");
      setIsCustomInstructionsModalOpen(true);
      return "custom_instructions_triggered";
    }
    return "not_applicable"; // e.g., Desktop browser with no specific instructions or already installed
  };

  const closeCustomInstructionsModal = () => {
    setIsCustomInstructionsModalOpen(false);
    setCustomPromptModalType(null);
  };

  return (
    <PWAInstallContext.Provider
      value={{
        setInstallPromptEvent,
        promptInstall,
        closeCustomInstructionsModal,
        customPromptModalType,
        isCustomInstructionsModalOpen,
        installPromptEvent,
        isPWAInstalled,
        pwaDisplayMode,
      }}
    >
      {children}
    </PWAInstallContext.Provider>
  );
};

export const usePWAInstall = () => {
  const context = useContext(PWAInstallContext);
  if (context === undefined) {
    throw new Error("usePWAInstall must be used within a PWAInstallProvider");
  }
  return context;
};
